import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import CrudPage from "@/components/CrudPage";
import { Btn } from "@/components/UI";
import { Robot, FileArrowUp, LinkSimple } from "@phosphor-icons/react";
import { SmartVisuraImport, SmartInvestimentiImport, SmartDocumentoImport } from "@/components/SmartImport";

const PROGRAMMI = ["Contratti di Filiera", "PNRR", "MASAF", "ISMEA", "GSE", "PSR", "Altro"];

export function FilierePage() {
  return <CrudPage
    endpoint="filiere" eyebrow="Anagrafica" title="Filiere / Progetti"
    testIdPrefix="filiere"
    columns={[
      { key: "nome", label: "Nome" },
      { key: "programma", label: "Programma" },
      { key: "cup", label: "CUP" },
      { key: "importo_totale", label: "Importo totale", currency: true },
      { key: "contributo_totale", label: "Contributo", currency: true },
      { key: "stato", label: "Stato" },
    ]}
    defaultItem={{
      programma: "Contratti di Filiera", stato: "attiva",
      ai_link_threshold_contratto: 15, ai_link_threshold_fattura: 20, ai_link_threshold_bonifico: 20,
      ai_amount_tolerance_contratto: 5, ai_amount_tolerance_fattura: 20, ai_amount_tolerance_bonifico: 0.5,
      ai_require_cup_match: false, ai_min_similarity_desc: 30,
    }}
    form={[
      { key: "nome", label: "Nome filiera", type: "text", required: true, colSpan: 2 },
      { key: "codice", label: "Codice", type: "text" },
      { key: "programma", label: "Programma", type: "select", options: PROGRAMMI.map((p) => ({ value: p, label: p })) },
      { key: "cup", label: "CUP", type: "text" },
      { key: "cig", label: "CIG", type: "text" },
      { key: "importo_totale", label: "Importo totale (€)", type: "number" },
      { key: "contributo_totale", label: "Contributo (€)", type: "number" },
      { key: "finanziamento_agevolato", label: "Finanziamento agevolato (€)", type: "number" },
      { key: "mezzi_propri", label: "Mezzi propri (€)", type: "number" },
      { key: "data_inizio", label: "Data inizio", type: "date" },
      { key: "data_fine", label: "Data fine", type: "date" },
      { key: "stato", label: "Stato", type: "select", options: ["attiva", "sospesa", "chiusa"].map((s) => ({ value: s, label: s })) },
      { key: "note", label: "Note", type: "textarea", colSpan: 2 },
      // AI tuning per bando
      { key: "ai_link_threshold_contratto", label: "🤖 Soglia min. link Contratto→Investimento (%)", type: "number" },
      { key: "ai_link_threshold_fattura", label: "🤖 Soglia min. link Fattura→Contratto (%)", type: "number" },
      { key: "ai_link_threshold_bonifico", label: "🤖 Soglia min. link Bonifico→Fattura (%)", type: "number" },
      { key: "ai_amount_tolerance_contratto", label: "🤖 Tolleranza importo Contratto (%)", type: "number" },
      { key: "ai_amount_tolerance_fattura", label: "🤖 Tolleranza importo Fattura (%)", type: "number" },
      { key: "ai_amount_tolerance_bonifico", label: "🤖 Tolleranza importo Bonifico (%)", type: "number" },
      { key: "ai_min_similarity_desc", label: "🤖 Similarità descrizione minima (%)", type: "number" },
      { key: "ai_require_cup_match", label: "🤖 Richiedi match CUP", type: "select", options: [{ value: "false", label: "No" }, { value: "true", label: "Sì" }] },
    ]}
  />;
}

function useFiliere() {
  const [f, setF] = useState([]);
  useEffect(() => { api.get("/filiere").then((r) => setF(r.data)); }, []);
  return f;
}

function useBeneficiari() {
  const [b, setB] = useState([]);
  useEffect(() => { api.get("/beneficiari").then((r) => setB(r.data)); }, []);
  return b;
}

function useInvestimenti() {
  const [i, setI] = useState([]);
  useEffect(() => { api.get("/investimenti").then((r) => setI(r.data)); }, []);
  return i;
}

function useFornitori() {
  const [f, setF] = useState([]);
  useEffect(() => { api.get("/fornitori").then((r) => setF(r.data)); }, []);
  return f;
}

export function BeneficiariPage() {
  const filiere = useFiliere();
  const [smartOpen, setSmartOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const defaultFiliera = filiere[0]?.id;
  return (
    <>
      <CrudPage
        key={reloadKey}
        endpoint="beneficiari" eyebrow="Anagrafica" title="Beneficiari"
        testIdPrefix="beneficiari"
        extraFilters={
          <Btn variant="accent" onClick={() => setSmartOpen(true)} data-testid="beneficiari-smart-import">
            <Robot size={16} weight="duotone" /> Carica Visura (AI)
          </Btn>
        }
        columns={[
          { key: "ragione_sociale", label: "Ragione sociale" },
          { key: "piva", label: "P.IVA" },
          { key: "forma_giuridica", label: "Forma" },
          { key: "referente", label: "Referente" },
          { key: "importo_ammesso", label: "Importo ammesso", currency: true },
          { key: "contributo_ammesso", label: "Contributo", currency: true },
        ]}
        form={[
          { key: "filiera_id", label: "Filiera", type: "select", required: true, options: filiere.map((f) => ({ value: f.id, label: f.nome })), colSpan: 2 },
          { key: "ragione_sociale", label: "Ragione sociale", type: "text", required: true, colSpan: 2 },
          { key: "piva", label: "P.IVA", type: "text" },
          { key: "codice_fiscale", label: "Codice fiscale", type: "text" },
          { key: "forma_giuridica", label: "Forma giuridica", type: "text" },
          { key: "capitale_sociale", label: "Capitale sociale (€)", type: "number" },
          { key: "rea", label: "REA", type: "text" },
          { key: "codice_ateco", label: "Codice ATECO", type: "text" },
          { key: "data_costituzione", label: "Data costituzione", type: "date" },
          { key: "pec", label: "PEC", type: "text" },
          { key: "indirizzo", label: "Indirizzo", type: "text", colSpan: 2 },
          { key: "referente", label: "Referente", type: "text" },
          { key: "email", label: "Email", type: "text" },
          { key: "telefono", label: "Telefono", type: "text" },
          { key: "importo_ammesso", label: "Importo ammesso (€)", type: "number" },
          { key: "contributo_ammesso", label: "Contributo ammesso (€)", type: "number" },
        ]}
      />
      <SmartVisuraImport open={smartOpen} onClose={() => setSmartOpen(false)}
        filieraId={defaultFiliera} onCreated={() => setReloadKey(reloadKey + 1)} />
    </>
  );
}

export function InvestimentiPage() {
  const filiere = useFiliere();
  const beneficiari = useBeneficiari();
  const [smartOpen, setSmartOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  return (
    <>
      <CrudPage
        key={reloadKey}
        endpoint="investimenti" eyebrow="Anagrafica" title="Investimenti approvati"
        testIdPrefix="investimenti"
        extraFilters={
          <Btn variant="accent" onClick={() => setSmartOpen(true)} data-testid="investimenti-smart-import">
            <FileArrowUp size={16} weight="duotone" /> Import Excel (AI)
          </Btn>
        }
        columns={[
          { key: "codice", label: "Codice" },
          { key: "descrizione", label: "Titolo" },
          { key: "tabella", label: "Tab" },
          { key: "categoria", label: "Categoria" },
          { key: "comune", label: "Comune" },
          { key: "totale", label: "Totale", currency: true },
          { key: "contributo", label: "Contributo", currency: true },
        ]}
        defaultItem={{ stato: "approvato" }}
        form={[
          { key: "filiera_id", label: "Filiera", type: "select", required: true, options: filiere.map((f) => ({ value: f.id, label: f.nome })) },
          { key: "beneficiario_id", label: "Beneficiario", type: "select", required: true, options: beneficiari.map((b) => ({ value: b.id, label: b.ragione_sociale })) },
          { key: "codice", label: "Codice investimento", type: "text", required: true },
          { key: "descrizione", label: "Titolo/Descrizione", type: "text", colSpan: 2 },
          { key: "tabella", label: "Tabella", type: "select", options: ["A", "B", "C", "D", "E"].map((t) => ({ value: t, label: `Tabella ${t}` })) },
          { key: "categoria", label: "Categoria", type: "text" },
          { key: "tag", label: "TAG", type: "text" },
          { key: "via", label: "Via", type: "text" },
          { key: "civico", label: "Civico", type: "text" },
          { key: "comune", label: "Comune", type: "text" },
          { key: "provincia", label: "Provincia", type: "text" },
          { key: "foglio", label: "Foglio", type: "text" },
          { key: "particella", label: "Particella/e", type: "text" },
          { key: "imponibile", label: "Imponibile (€)", type: "number" },
          { key: "iva", label: "IVA (€)", type: "number" },
          { key: "totale", label: "Totale/Importo approvato (€)", type: "number" },
          { key: "contributo", label: "Contributo (€)", type: "number" },
          { key: "finanziamento_agevolato", label: "Finanziamento agevolato (€)", type: "number" },
          { key: "mezzi_propri", label: "Mezzi propri (€)", type: "number" },
          { key: "stato", label: "Stato", type: "select", options: ["approvato", "avviato", "completato", "revocato"].map((s) => ({ value: s, label: s })) },
          { key: "note", label: "Note", type: "textarea", colSpan: 2 },
        ]}
      />
      <SmartInvestimentiImport open={smartOpen} onClose={() => setSmartOpen(false)}
        filiere={filiere} beneficiari={beneficiari} onCreated={() => setReloadKey(reloadKey + 1)} />
    </>
  );
}

export function FornitoriPage() {
  return <CrudPage
    endpoint="fornitori" eyebrow="Anagrafica" title="Fornitori"
    testIdPrefix="fornitori"
    columns={[
      { key: "ragione_sociale", label: "Ragione sociale" },
      { key: "piva", label: "P.IVA" },
      { key: "email", label: "Email" },
      { key: "telefono", label: "Telefono" },
    ]}
    form={[
      { key: "ragione_sociale", label: "Ragione sociale", type: "text", required: true, colSpan: 2 },
      { key: "piva", label: "P.IVA", type: "text" },
      { key: "codice_fiscale", label: "Codice fiscale", type: "text" },
      { key: "indirizzo", label: "Indirizzo", type: "text", colSpan: 2 },
      { key: "email", label: "Email", type: "text" },
      { key: "telefono", label: "Telefono", type: "text" },
      { key: "iban", label: "IBAN", type: "text", colSpan: 2 },
      { key: "note", label: "Note", type: "textarea", colSpan: 2 },
    ]}
  />;
}

export function DocumentiPage() {
  const filiere = useFiliere();
  const beneficiari = useBeneficiari();
  const investimenti = useInvestimenti();
  const fornitori = useFornitori();
  const [smartOpen, setSmartOpen] = useState(false);
  const [smartType, setSmartType] = useState("contratto");
  const [reloadKey, setReloadKey] = useState(0);

  const openSmart = (type) => { setSmartType(type); setSmartOpen(true); };

  return (
    <>
      <CrudPage
        key={reloadKey}
        endpoint="documenti" eyebrow="Rendicontazione" title="Documenti"
        testIdPrefix="documenti"
        extraFilters={
          <div className="flex items-center gap-2">
            <Btn variant="accent" onClick={() => openSmart("contratto")} data-testid="doc-import-contratto">
              <LinkSimple size={16} weight="duotone" /> Contratto (AI)
            </Btn>
            <Btn variant="accent" onClick={() => openSmart("preventivo")} data-testid="doc-import-preventivo">
              <LinkSimple size={16} weight="duotone" /> Preventivo (AI)
            </Btn>
            <Btn variant="accent" onClick={() => openSmart("fattura")} data-testid="doc-import-fattura">
              <LinkSimple size={16} weight="duotone" /> Fattura (AI)
            </Btn>
          </div>
        }
        columns={[
          { key: "tipo", label: "Tipo" },
          { key: "numero", label: "Numero" },
          { key: "data", label: "Data", date: true },
          { key: "oggetto", label: "Oggetto" },
          { key: "totale", label: "Totale", currency: true },
          { key: "cup", label: "CUP" },
        ]}
        form={[
          { key: "filiera_id", label: "Filiera", type: "select", required: true, options: filiere.map((f) => ({ value: f.id, label: f.nome })) },
          { key: "beneficiario_id", label: "Beneficiario", type: "select", options: beneficiari.map((b) => ({ value: b.id, label: b.ragione_sociale })) },
          { key: "investimento_id", label: "Investimento", type: "select", options: investimenti.map((i) => ({ value: i.id, label: i.codice + " - " + (i.descrizione || "") })) },
          { key: "fornitore_id", label: "Fornitore", type: "select", options: fornitori.map((f) => ({ value: f.id, label: f.ragione_sociale })) },
          { key: "tipo", label: "Tipo documento", type: "select", required: true,
            options: ["contratto", "preventivo", "offerta", "oda", "ddt", "lettera_incarico", "fattura", "bonifico", "altro"].map((t) => ({ value: t, label: t })) },
          { key: "numero", label: "Numero", type: "text" },
          { key: "data", label: "Data", type: "date" },
          { key: "oggetto", label: "Oggetto", type: "text", colSpan: 2 },
          { key: "imponibile", label: "Imponibile (€)", type: "number" },
          { key: "iva", label: "IVA (€)", type: "number" },
          { key: "totale", label: "Totale (€)", type: "number" },
          { key: "cup", label: "CUP", type: "text" },
          { key: "cig", label: "CIG", type: "text" },
          { key: "dicitura_pnrr", label: "Dicitura PNRR", type: "text", colSpan: 2 },
          { key: "modalita_pagamento", label: "Modalità pagamento", type: "text" },
          { key: "note", label: "Note", type: "textarea", colSpan: 2 },
        ]}
      />
      <SmartDocumentoImport open={smartOpen} onClose={() => setSmartOpen(false)}
        docType={smartType}
        filiere={filiere} beneficiari={beneficiari} fornitori={fornitori}
        onCreated={() => setReloadKey(reloadKey + 1)} />
    </>
  );
}

export function BonificiPage() {
  const filiere = useFiliere();
  const beneficiari = useBeneficiari();
  const fornitori = useFornitori();
  const [smartOpen, setSmartOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  return (
    <>
      <CrudPage
        key={reloadKey}
        endpoint="bonifici" eyebrow="Rendicontazione" title="Bonifici & Pagamenti"
        testIdPrefix="bonifici"
        extraFilters={
          <Btn variant="accent" onClick={() => setSmartOpen(true)} data-testid="bonifici-smart-import">
            <LinkSimple size={16} weight="duotone" /> Bonifico (AI)
          </Btn>
        }
        columns={[
          { key: "data", label: "Data", date: true },
          { key: "importo", label: "Importo", currency: true },
          { key: "cro_trn", label: "CRO/TRN" },
          { key: "causale", label: "Causale" },
        ]}
        form={[
          { key: "filiera_id", label: "Filiera", type: "select", required: true, options: filiere.map((f) => ({ value: f.id, label: f.nome })) },
          { key: "beneficiario_id", label: "Beneficiario", type: "select", options: beneficiari.map((b) => ({ value: b.id, label: b.ragione_sociale })) },
          { key: "fornitore_id", label: "Fornitore beneficiario bonifico", type: "select", options: fornitori.map((f) => ({ value: f.id, label: f.ragione_sociale })) },
          { key: "data", label: "Data bonifico", type: "date", required: true },
          { key: "importo", label: "Importo (€)", type: "number", required: true },
          { key: "cro_trn", label: "CRO/TRN", type: "text" },
          { key: "causale", label: "Causale", type: "text", colSpan: 2 },
          { key: "note", label: "Note", type: "textarea", colSpan: 2 },
        ]}
      />
      <SmartDocumentoImport open={smartOpen} onClose={() => setSmartOpen(false)}
        docType="bonifico"
        filiere={filiere} beneficiari={beneficiari} fornitori={fornitori}
        onCreated={() => setReloadKey(reloadKey + 1)} />
    </>
  );
}

export function SalPageCrud() {
  const filiere = useFiliere();
  return <CrudPage
    endpoint="sal" eyebrow="Rendicontazione" title="SAL & Dossier finali"
    testIdPrefix="sal"
    columns={[
      { key: "numero", label: "SAL n." },
      { key: "data", label: "Data", date: true },
      { key: "importo", label: "Importo", currency: true },
      { key: "stato", label: "Stato" },
    ]}
    defaultItem={{ stato: "bozza", numero: 1 }}
    form={[
      { key: "filiera_id", label: "Filiera", type: "select", required: true, options: filiere.map((f) => ({ value: f.id, label: f.nome })) },
      { key: "numero", label: "Numero SAL", type: "number", required: true },
      { key: "data", label: "Data", type: "date", required: true },
      { key: "importo", label: "Importo (€)", type: "number" },
      { key: "stato", label: "Stato", type: "select", options: ["bozza", "inviato", "approvato", "erogato"].map((s) => ({ value: s, label: s })) },
      { key: "note", label: "Note", type: "textarea", colSpan: 2 },
    ]}
  />;
}
